/** Local-day hourly evidence. Repeated clock hours are ambiguous without UTC
 * timestamps, so omit every duplicate rather than choosing an arbitrary score.
 * This also makes rollups deterministic for shuffled provider responses. */
export function uniqueOrderedHours<T extends { hour: number }>(hours: readonly T[]): T[] {
  const counts = new Map<number, number>();
  for (const point of hours) counts.set(point.hour, (counts.get(point.hour) ?? 0) + 1);
  return hours.filter((point) =>
    Number.isInteger(point.hour) && point.hour >= 0 && point.hour < 24 &&
    counts.get(point.hour) === 1,
  ).sort((a, b) => a.hour - b.hour);
}

export function consecutiveHourlyWindows<T extends { hour: number }>(
  hours: readonly T[],
  duration: number,
): T[][] {
  if (!Number.isInteger(duration) || duration < 1) return [];
  const ordered = uniqueOrderedHours(hours);
  const windows: T[][] = [];
  for (let i = 0; i <= ordered.length - duration; i++) {
    const window = ordered.slice(i, i + duration);
    if (window.every((point, j) => j === 0 || point.hour === window[j - 1].hour + 1)) {
      windows.push(window);
    }
  }
  return windows;
}

export type DailyQualityPoint = { hour: number; score: number | null; isDay: boolean };

/** All arithmetic stays on the caller's scale. No supported continuous window
 * means an available-hour mean, with no invented session start or peak bonus. */
export function summariseDailyQuality(
  hours: readonly DailyQualityPoint[],
  duration: number,
  bestWeight: number,
) {
  const usable = uniqueOrderedHours(hours).filter(
    (point): point is DailyQualityPoint & { score: number } =>
      point.isDay && typeof point.score === "number" && Number.isFinite(point.score),
  );
  const daylightAvg = usable.length
    ? usable.reduce((sum, point) => sum + point.score, 0) / usable.length
    : 0;
  const windows = consecutiveHourlyWindows(usable, duration);
  let best: { start: number; average: number; scores: number[] } | null = null;
  for (const window of windows) {
    const average = window.reduce((sum, point) => sum + point.score, 0) / duration;
    if (best === null || average > best.average) {
      best = { start: window[0].hour, average, scores: window.map((point) => point.score) };
    }
  }
  return {
    overall: best === null ? daylightAvg : bestWeight * best.average + (1 - bestWeight) * daylightAvg,
    daylightAvg,
    bestWindowAvg: best?.average ?? null,
    bestWindowStart: best?.start ?? null,
    bestWindowScores: best?.scores ?? [],
    availableHours: usable.length,
  };
}
